import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["form", "fileInput", "report", "validateBtn", "applyBtn", "modal"]

  connect() {
    this.modalTarget?.addEventListener('hidden.bs.modal', () => this.resetModal())
  }

  disconnect() {
    this.modalTarget?.removeEventListener('hidden.bs.modal', () => this.resetModal())
  }

  resetModal() {
    this.formTarget?.reset()
    if (this.reportTarget) this.reportTarget.innerHTML = ''
    this.applyBtnTarget?.classList.add('d-none')
    this.resetButton(this.validateBtnTarget, 'Validate')
    window.location.reload()
  }

  onFileChange() {
    if (this.reportTarget) this.reportTarget.innerHTML = ''
    this.applyBtnTarget?.classList.add('d-none')
  }

  resetButton(button, text) {
    if (!button) return
    button.disabled = false
    button.textContent = text
  }

  setButtonLoading(button, text) {
    if (!button) return
    button.disabled = true
    button.textContent = text
  }

  scrollableDiv(content) {
    return `<div style="max-height: 300px; overflow-y: auto; margin-top: 10px;">${content}</div>`
  }

  formatRowError(rowError) {
    return rowError.row
      ? `Row ${rowError.row}: ${rowError.errors.join('; ')}`
      : rowError.errors.join('; ')
  }

  formatErrorsList(errors_by_row) {
    return errors_by_row.map(rowError => this.formatRowError(rowError)).join('<br>')
  }

  validate(e) {
    e.preventDefault()

    if (!this.fileInputTarget.files.length) {
      alert('Please select an XLSX file')
      return
    }

    const file = this.fileInputTarget.files[0]
    if (!file.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
      alert('Please select a valid XLSX or XLS file')
      return
    }

    this.onFileChange()
    this.setButtonLoading(this.validateBtnTarget, 'Validating...')

    fetch(this.formTarget.action, {
      method: 'POST',
      body: new FormData(this.formTarget),
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
    })
    .then(response => this.parseJson(response))
    .then(({ response, data }) => {
      if (response.ok) {
        this.displayReport(data)
      } else {
        this.displayUploadError(data.errors || ['Validation failed. Please try again.'])
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Validation failed. Please try again.')
    })
    .finally(() => this.resetButton(this.validateBtnTarget, 'Validate'))
  }

  displayReport(data) {
    const errorsList = data.errors_by_row?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(data.errors_by_row))}
        </div>`
      : ''

    const readyMessage = data.to_deactivate > 0
      ? `<div class="alert alert-warning"><strong>Warning:</strong> ${data.errors_count === 0 ? 'Validation passed!' : 'Some rows have errors, but valid users can still be deactivated.'} Ready to deactivate ${data.to_deactivate} user(s) and unassign their credentials.</div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert ${data.to_deactivate > 0 ? 'alert-success' : 'alert-danger'} mb-3">
        <strong>Summary:</strong><br>
        Users to Deactivate: ${data.to_deactivate}<br>
        Errors: ${data.errors_count}
      </div>
      ${errorsList}
      ${readyMessage}
    `

    this.applyBtnTarget?.classList[data.to_deactivate > 0 ? 'remove' : 'add']('d-none')
  }

  apply(e) {
    e.preventDefault()

    if (!confirm('Are you sure you want to deactivate these users? This will release their credentials.')) return

    this.setButtonLoading(this.applyBtnTarget, 'Deactivating...')

    fetch('/admin/users_bulk/apply_deactivate', {
      method: 'POST',
      body: new FormData(this.formTarget),
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => this.parseJson(response))
    .then(({ response, data }) => {
      if (response.ok) {
        data.success ? this.displayDeactivateResults(data) : this.displayDeactivateErrors(data)
      } else {
        this.displayUploadError(data.errors || [data.message || 'Deactivation failed.'])
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Deactivation failed. Please try again.')
    })
    .finally(() => this.resetButton(this.applyBtnTarget, 'Apply Deactivation'))
  }

  displayDeactivateResults(data) {
    const { summary, successes, failures } = data

    const successSection = successes?.length > 0
      ? `<div class="alert alert-info">
          <strong>Deactivated Users:</strong>
          ${this.scrollableDiv(successes.map(item =>
            `Row ${item.row}: ${item.name} (${item.email})`
          ).join('<br>'))}
        </div>`
      : ''

    const failedSection = failures?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Failed:</strong>
          ${this.scrollableDiv(failures.map(item =>
            `Row ${item.row}: ${item.email} - ${item.error}`
          ).join('<br>'))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-success mb-3">
        <strong>Deactivation Completed!</strong><br>
        Deactivated: ${summary.deactivated}; Failed: ${summary.failed}
      </div>
      ${successSection}
      ${failedSection}
    `

    this.applyBtnTarget?.classList.add('d-none')
    this.validateBtnTarget?.classList.remove('d-none')
    this.resetButton(this.validateBtnTarget, 'Validate')

    // Reload page after 5 seconds to show updated user list
    // setTimeout(() => window.location.reload(), 5000)
  }

  displayDeactivateErrors(data) {
    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        <strong>Deactivation Failed</strong><br>
        ${data.message}
      </div>
    `
  }

  parseJson(response) {
    return response.json()
      .then(data => ({ response, data }))
      .catch(() => ({ response, data: {} }))
  }

  displayUploadError(errors) {
    if (!this.reportTarget) return

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        ${errors.join('<br>')}
      </div>
    `

    this.applyBtnTarget?.classList.add('d-none')
  }
}
